import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, Download } from "lucide-react";

export default function PaymentSuccess() {
  const { t } = useI18n();
  const { refresh } = useAuth();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling");
  const [productId, setProductId] = useState(null);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setProductId(data.product_id);
        if (data.payment_status === "paid") {
          setStatus("paid");
          refresh();
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) { setStatus("error"); return; }
      } catch { /* keep polling */ }
      if (tries++ < 12) setTimeout(poll, 2000);
      else setStatus("timeout");
    };
    poll();
  }, [sessionId, refresh]);

  return (
    <div className="max-w-md mx-auto px-5 py-24 text-center">
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        className="bg-white border border-[#E2E8F0] rounded-3xl p-10">
        {status === "polling" && (
          <>
            <Loader2 className="h-12 w-12 animate-spin text-[#0EA5E9] mx-auto mb-5" />
            <p className="text-[#475569]" data-testid="pay-processing">{t("pay.processing")}</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="h-16 w-16 text-emerald-500 mx-auto mb-5" />
            <h1 className="font-display text-3xl font-extrabold text-[#0F172A]" data-testid="pay-success">{t("pay.success")}</h1>
            <p className="text-[#475569] mt-3">{t("pay.successText")}</p>
            <Link to="/purchases">
              <Button className="mt-7 rounded-full bg-[#0F172A] hover:bg-[#0EA5E9] text-white font-bold" data-testid="pay-go-purchases">
                <Download className="h-4 w-4 mr-2" /> {t("pay.goPurchases")}
              </Button>
            </Link>
          </>
        )}
        {(status === "error" || status === "timeout") && (
          <>
            <p className="text-[#475569]">{t("pay.processing")}</p>
            <Link to="/purchases"><Button variant="outline" className="mt-6 rounded-full">{t("pay.goPurchases")}</Button></Link>
          </>
        )}
      </motion.div>
    </div>
  );
}
